import React, {useCallback, useMemo, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {Tag} from './right-contents';
import {useLanguageModal} from '@/components/business/language';
import {useGoTo} from '../me-header/hooks';
import {goCS} from '@/utils';
import {useConfirm} from '@/components/basic/modal';
import {trackEvent} from '@/utils/track-event';
import {setUniqueId} from '@/utils/moengage';
import {toLogin} from '../../me.variable';
import {useVersionModal} from '@/common-pages/hooks/versionmodal.hooks';
import {getVersionName, inApp} from '@/utils/interface';

export interface MeListItemData {
  icon: string;
  title: string;
  rightContent?: React.ReactNode;
  onClick?: () => void;
}

export function useMeList() {
  const {t} = useTranslation();
  const {renderModal: renderLanguageModal, show: languageShow} =
    useLanguageModal();
  const {versionModal, handleUpdate} = useVersionModal(false);
  const giftModalRef = useRef<any>();
  const {
    login,
    toBet,
    toProxy,
    toCommission,
    toRecharge,
    toWithdraw,
    toTransactions,
    toPassword,
  } = useGoTo();

  const requireLogin = useCallback(
    (action: () => void) => () => {
      if (!login) {
        toLogin();
        return;
      }
      action();
    },
    [login],
  );

  const list = useMemo(() => {
    const baseList: MeListItemData[] = [
      {
        icon: require('@/assets/icons/me/item/gift-code.webp'),
        title: t('me.bottom.gifCode'),
        rightContent: <Tag isHot>HOT</Tag>,
        onClick: requireLogin(() => {
          giftModalRef.current?.show();
        }),
      },
      {
        icon: require('@/assets/icons/me/item/my-lotteries.webp'),
        title: t('me.bottom.myLottery'),
        onClick: requireLogin(toBet),
      },
      {
        icon: require('@/assets/icons/me/item/agency-center.webp'),
        title: t('me.bottom.proxy'),
        rightContent: <Tag>NEW</Tag>,
        onClick: requireLogin(toProxy),
      },
      {
        icon: require('@/assets/icons/me/item/my-commission.webp'),
        title: t('me.bottom.commission'),
        onClick: requireLogin(toCommission),
      },
      {
        icon: require('@/assets/icons/me/item/add-funds.webp'),
        title: t('me.bottom.addFunds'),
        onClick: requireLogin(toRecharge),
      },
      {
        icon: require('@/assets/icons/me/item/withdraw-funds.webp'),
        title: t('me.bottom.withdrawFunds'),
        onClick: requireLogin(toWithdraw),
      },
      {
        icon: require('@/assets/icons/me/item/my-transactions.webp'),
        title: t('me.bottom.myTransactions'),
        onClick: requireLogin(toTransactions),
      },
      {
        icon: require('@/assets/icons/me/item/customer-service.webp'),
        title: t('me.bottom.customer'),
        onClick: goCS,
      },
      {
        icon: require('@/assets/icons/me/item/password.webp'),
        title: t('me.bottom.password'),
        onClick: requireLogin(toPassword),
      },
      {
        icon: require('@/assets/icons/me/item/languages.webp'),
        title: t('me.bottom.lang'),
        onClick: languageShow,
      },
    ];

    if (inApp) {
      baseList.push({
        icon: require('@/assets/icons/me/item/update.webp'),
        title: t('me.bottom.update'),
        rightContent: (
          <span className="text-t3 text-xs font-medium">
            {getVersionName()}
          </span>
        ),
        onClick: handleUpdate,
      });
    }

    return baseList;
  }, [
    t,
    toBet,
    toProxy,
    toCommission,
    toRecharge,
    toWithdraw,
    toTransactions,
    toPassword,
    languageShow,
    handleUpdate,
    requireLogin,
  ]);

  const {renderModal: renderConfirmModal, show: confirmShow} = useConfirm();

  const handleLogoutButton = useCallback(() => {
    confirmShow(t('alert.logout'), t('alert.sureLogout'), () => {
      trackEvent('logout');
      setUniqueId('');
      toLogin();
    });
  }, [confirmShow, t]);

  return {
    list,
    renderLanguageModal,
    giftModalRef,
    handleLogoutButton,
    renderConfirmModal,
    renderVersionModal: versionModal.renderModal,
    t,
  };
}
